import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Adult, Bags, BigShield, CarCompany, CarName, ExtraSeat, GreenTick, Window } from '../../../assests/ExportImageIcon'
// StarWhite was only used by the commented-out rating badge / Supplier Rating section
import '../../hotel/hotel-details/hotelDetail.css'
import '../carListing/carResults.css'
import './carDetail.css'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import { setCarBookingContext, setCarDetails } from '../../../redux/carRentalSearch/carRentalSearchSlice'
import { formatMoney } from '../../../utils/formatMoney'
import CarSearchLoader from '../../../componants/carSearchLoader/CarSearchLoader'

const formatConditionTitle = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const DOCUMENTS_REQUIRED = ['Passport or ID card', "Driver's License", 'PAN Card']

// const SUPPLIER_RATING = { rating: 4.5, label: 'Very Good', brand: CarCompany } // only used by the commented-out Supplier Rating section

const EXTRAS = [
    { icon: ExtraSeat, title: 'Vehicle Charges' },
    { icon: BigShield, title: 'Coverages' },
]

const CarDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const selectedCar = useSelector((state) => state.carRentalSearch.selectedCar)
    const details = useSelector((state) => state.carRentalSearch.details)
    const searchParams = useSelector((state) => state.carRentalSearch.searchParams)
    const [loading, setLoading] = useState(false)
    const [conditionsOpen, setConditionsOpen] = useState(false)

    useEffect(() => {
        if (!selectedCar) return
        setLoading(true)
        service
            .post(endpoints.cars.details, {
                session_id: selectedCar.session_id,
                reference_id: selectedCar.reference_id,
            }, { timeout: 45000 })
            .then(({ data }) => {
                dispatch(setCarDetails(data.data))
            })
            .catch(() => {
                dispatch(setCarDetails(null))
            })
            .finally(() => setLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCar])

    if (!selectedCar) {
        return (
            <section className='allSearchArea commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <p>No car selected. Please go back and search again.</p>
                    <button type='button' className='commonBtn' onClick={() => navigate('/car-list')}>Search Cars</button>
                </div>
            </section>
        )
    }

    if (loading) {
        return (
            <section className='allSearchArea commonTopPadding commonBottomPadding'>
                <div className='container'>
                    <CarSearchLoader text='Loading car details...' />
                </div>
            </section>
        )
    }

    const apiCarDetails = details?.car_details
    const fareDetails = details?.fare_details
    const rentalConditions = details?.rental_conditions

    const CAR_INFO = {
        rating: 4.5,
        brand: CarCompany,
        title: apiCarDetails?.name || selectedCar.car_details?.car_name || 'Car',
        code: apiCarDetails?.code || selectedCar.car_details?.car_code || '',
        transmission: apiCarDetails?.transmission || selectedCar.car_details?.transmission_type || '',
        adults: apiCarDetails?.passengers ?? selectedCar.car_details?.passenger_quantity ?? '-',
        bags: apiCarDetails?.baggage ?? selectedCar.car_details?.baggage_quantity ?? '-',
        windows: selectedCar.car_details?.vehicle_door ?? '-',
        fuelType: apiCarDetails?.fuel_type || '-',
        airConditioning: apiCarDetails?.air_conditioning === true || apiCarDetails?.air_conditioning === 'true'
            ? 'Yes'
            : (apiCarDetails?.air_conditioning === false || apiCarDetails?.air_conditioning === 'false' ? 'No' : '-'),
        image: apiCarDetails?.image_url || selectedCar.car_details?.car_image_hd || selectedCar.car_details?.car_image || CarName,
    }

    const summary = fareDetails?.summary
    const FARE = {
        title: 'Fare Breakdown',
        vehicleCharge: formatMoney(summary?.vehicle_charge, fareDetails?.currency),
        coverageCharge: formatMoney(summary?.coverage_charge, fareDetails?.currency),
        extrasCharge: formatMoney(summary?.optional_extra_charge, fareDetails?.currency),
        total: formatMoney(summary?.total_fare, fareDetails?.currency),
        note: 'Convenience fee is charged for payment processing and platform services. Seat selection charges (if any) will be added during seat selection.',
    }

    const includedOffer = rentalConditions?.included_in_rate?.length ? rentalConditions.included_in_rate : ['Unlimited mileage', 'Collision Damage Waiver', 'Theft Protection', 'Online Check-in']

    // Every other rental_conditions field the API returns (driving_licence, deposit_and_payment_card_information,
    // insurance_coverage, breakdown_assistance, mileage_policy, special_equipment_optional_extras, etc.) —
    // included_in_rate/drivers_age/fuel_policy are already shown above, so they're skipped here.
    const ALREADY_SHOWN_CONDITION_KEYS = ['included_in_rate', 'drivers_age', 'fuel_policy']
    const rentalConditionsExtra = Object.entries(rentalConditions || {}).filter(
        ([key, value]) => !ALREADY_SHOWN_CONDITION_KEYS.includes(key) && Array.isArray(value) && value.length > 0
    )

    const pickupLocation = searchParams.pickupLocation
    const dropoffLocation = searchParams.dropoffLocation
    const PICKUP_DROPOFF = [
        { label: 'Pick-up Point:', address: pickupLocation?.location_name || 'Not specified', lines: pickupLocation?.address ? [pickupLocation.address] : [], datetime: selectedCar.pickup || '' },
        { label: 'Drop-off Point:', address: dropoffLocation?.location_name || 'Not specified', lines: dropoffLocation?.address ? [dropoffLocation.address] : [], datetime: selectedCar.dropoff || '' },
    ]

    const handleRequestProposal = () => {
        dispatch(setCarBookingContext({
            session_id: selectedCar.session_id,
            reference_id: selectedCar.reference_id,
            fare_summary: summary,
            total_label: FARE.total,
            currency: fareDetails?.currency,
        }))
        navigate('/car-booking-detail')
    }

    return (
        <>
            <section className='allSearchArea commonTopPadding'>
                <div className='container'>
                    <div className='row g-4'>

                        <div className='col-md-12'>
                            <div className='hotelDetailGrid carDetail__topGrid'>
                                <div className='hotelDetailLeft'>
                                    <div className='carResultCard bgWhite commonBorder borderRadius20'>
                                        {/* <div className='carResultCard__top'>
                                            <span className='hotelCard__ratingBadge'>
                                                <img src={StarWhite} alt="" /> {CAR_INFO.rating}
                                            </span>
                                            <img src={CAR_INFO.brand} alt="" className='carResultCard__brand' />
                                        </div> */}

                                        <img src={CAR_INFO.image} alt={CAR_INFO.title} className='carResultCard__image' />

                                        <div className='carResultCard__title mt-0'>{CAR_INFO.title}{CAR_INFO.code ? ` (${CAR_INFO.code})` : ''}</div>
                                        <div className='carResultCard__subtitle'>{CAR_INFO.transmission}</div>

                                        <div className='carResultCard__specs'>
                                            <span><img src={Adult} alt="" /> {CAR_INFO.adults} Adults</span>
                                            <span><img src={Bags} alt="" /> {CAR_INFO.bags} Bags</span>
                                            <span><img src={Window} alt="" /> {CAR_INFO.windows} Doors</span>
                                        </div>

                                        {/* <div className='bsFareRow'>
                                            <span>Fuel Type:</span>
                                            <span>{CAR_INFO.fuelType}</span>
                                        </div>
                                        <div className='bsFareRow'>
                                            <span>Air Conditioning:</span>
                                            <span>{CAR_INFO.airConditioning}</span>
                                        </div> */}
                                    </div>

                                    <div className='carDetailGrid'>
                                        <div>
                                            <div className='bsSectionTitle'>Included in your offer</div>
                                            <div className='bsBox'>
                                                <ul className='carDetailChecklist'>
                                                    {includedOffer.map((item, index) => (
                                                        <li key={index}><img src={GreenTick} alt="" /> {item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                        <div>
                                            <div className='bsSectionTitle'>Documents Required</div>
                                            <div className='bsBox'>
                                                <ul className='carDetailChecklist'>
                                                    {DOCUMENTS_REQUIRED.map((item, index) => (
                                                        <li key={index}><img src={GreenTick} alt="" /> {item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {apiCarDetails?.similar_vehicles?.length > 0 && (
                                        <div className='col-md-12'>
                                            <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                                <div className='bsSectionTitle mb-2'>Available Upgrades</div>
                                                <p className='carDetail__upgradeNote'>Similar vehicles you might also like:</p>
                                                <ul className='carDetailChecklist carDetailChecklist--twoCol'>
                                                    {apiCarDetails.similar_vehicles.map((vehicle, index) => (
                                                        <li key={index}><img src={GreenTick} alt="" /> {vehicle}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className='hotelDetailRight'>
                                    <div className='bsBox carDetail__fareBox'>
                                        <div className='bsSectionTitle'>{FARE.title}</div>

                                        {!fareDetails && <p>Unable to load fare details. Please try again.</p>}

                                        {fareDetails && (
                                            <>
                                                <div className='bsFareRow'>
                                                    <span>Vehicle Charge</span>
                                                    <span>{FARE.vehicleCharge}</span>
                                                </div>
                                                <div className='bsFareRow'>
                                                    <span>Coverage Charge</span>
                                                    <span>{FARE.coverageCharge}</span>
                                                </div>
                                                <div className='bsFareRow'>
                                                    <span>Optional Extras</span>
                                                    <span>{FARE.extrasCharge}</span>
                                                </div>

                                                {fareDetails.optional_extras?.length > 0 && (
                                                    <>
                                                        <div className='bsFareDivider' />
                                                        <div className='bsSectionTitle fareBaggageTitle'>Optional Extras</div>
                                                        {fareDetails.optional_extras.map((extra, index) => (
                                                            <div className='bsFareRow' key={index}>
                                                                <span>{extra.name}{extra.included ? '' : ' (optional)'}</span>
                                                                <span>{formatMoney(extra.amount, fareDetails.currency)}</span>
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                                <div className='bsFareDivider' />
                                                <div className='bsFareRow bsFareRow--total'>
                                                    <span>TOTAL AMOUNT</span>
                                                    <span>{FARE.total}</span>
                                                </div>

                                                {/* {fareDetails.vehicle_charges?.length > 0 && (
                                                    <>
                                                        <div className='bsFareDivider' />
                                                        <div className='bsSectionTitle fareBaggageTitle'>Vehicle Charges</div>
                                                        {fareDetails.vehicle_charges.map((charge, index) => (
                                                            <div className='bsFareRow' key={index}>
                                                                <span>{charge.name}</span>
                                                                <span>{formatMoney(charge.amount, fareDetails.currency)}</span>
                                                            </div>
                                                        ))}
                                                    </>
                                                )}

                                                {fareDetails.coverages?.length > 0 && (
                                                    <>
                                                        <div className='bsFareDivider' />
                                                        <div className='bsSectionTitle fareBaggageTitle'>Coverages</div>
                                                        {fareDetails.coverages.map((coverage, index) => (
                                                            <div className='bsFareRow' key={index}>
                                                                <span>{coverage.name}{coverage.included ? '' : ' (optional)'}</span>
                                                                <span>{formatMoney(coverage.amount, fareDetails.currency)}</span>
                                                            </div>
                                                        ))}
                                                    </>
                                                )} */}


                                            </>
                                        )}
                                        <button type='button' className='commonBtn w-100' onClick={handleRequestProposal} disabled={!fareDetails}>Request Proposal</button>
                                        <p className='bsFareNote'>{FARE.note}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* <div className='col-md-12'>
                            <button type='button' className='bgWhite travellerAddBtn carDetail__conditionsBtn '>See All Rental Conditions</button>
                        </div> */}

                        {/* <div className='col-md-12'>
                            <div className='bsSectionTitle'>Supplier Rating</div>
                            <div className='bgWhite commonBorder borderRadius20 paddingCommon20 carDetail__supplierRow'>
                                <span className='hotelCard__ratingBadge'>
                                    <img src={StarWhite} alt="" /> {SUPPLIER_RATING.rating}
                                </span>
                                <span className='carDetail__supplierLabel'>{SUPPLIER_RATING.label}</span>
                                <img src={SUPPLIER_RATING.brand} alt="" className='carResultCard__brand carDetail__supplierBrand' />
                            </div>
                        </div> */}



                        <div className='col-md-12'>
                            <div className='carDetailGrid'>
                                {EXTRAS.map((extra, index) => (
                                    <div className='bgWhite commonBorder borderRadius20 paddingCommon20 carDetail__extraCard' key={index}>
                                        <div className='carDetail__extraHead'>
                                            <div>
                                                <div className='carDetail__extraTitle'>{extra.title}</div>
                                                <div className='carDetail__extraDesc'>{extra.desc}</div>
                                            </div>
                                            <span className='carDetail__extraIcon'>
                                                <img src={extra.icon} alt="" />
                                            </span>
                                        </div>

                                        {extra.title === 'Vehicle Charges' && fareDetails?.vehicle_charges?.length > 0 && (
                                            <div className='carDetail__extraChargesList'>
                                                {fareDetails.vehicle_charges.map((charge, chargeIndex) => (
                                                    <div className='bsFareRow' key={chargeIndex}>
                                                        <span>{charge.name}</span>
                                                        <span>{formatMoney(charge.amount, fareDetails.currency)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {extra.title === 'Coverages' && fareDetails?.coverages?.length > 0 && (
                                            <div className='carDetail__extraChargesList'>
                                                {fareDetails.coverages.map((coverage, coverageIndex) => (
                                                    <div className='bsFareRow' key={coverageIndex}>
                                                        <span>{coverage.name}</span>
                                                        <span>{formatMoney(coverage.amount, fareDetails.currency)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className='bsSectionTitle'>Pick-up and drop-off</div>
                            <div className='carDetail__pickupBox bgWhite commonBorder'>
                                <div className='carDetail__pickupGrid'>
                                    {PICKUP_DROPOFF.map((point, index) => (
                                        <div key={index} className='carDetail__pickupCol'>
                                            <div className='carDetail__pickupLabel'>{point.label}</div>
                                            <div className='carDetail__pickupAddress'>{point.address}</div>
                                            {point.lines.map((line, lineIndex) => (
                                                <div className='carDetail__pickupLine' key={lineIndex}>{line}</div>
                                            ))}
                                            <div className='carDetail__pickupTime'>{point.datetime}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className='carDetail__pickupInstruction commonGredientText'>View Pick-up &amp; Drop-off Instruction</div>
                            </div>
                        </div>

                        <div className='col-md-12'>
                            <div className='bsSectionTitle'>Important to know</div>
                            <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                <div className='carDetail__infoBlock'>
                                    <div className='carDetail__infoTitle'>There is a security deposit</div>
                                    <p className='carDetail__infoDesc'>The supplier will hold/charge a deposit on the main driver&apos;s credit card at pick-up. If no charges are incurred after the rental, it will be released.</p>
                                </div>

                                <div className='carDetail__infoBlock'>
                                    <div className='carDetail__infoTitle'>Bring your documents</div>
                                    <ul className='carDetailChecklist'>
                                        <li><img src={GreenTick} alt="" /> Passport or ID card</li>
                                        <li><img src={GreenTick} alt="" /> Driver&apos;s license</li>
                                    </ul>
                                </div>

                                {rentalConditions?.drivers_age?.length > 0 && (
                                    <div className='carDetail__infoBlock'>
                                        <div className='carDetail__infoTitle'>Driver's age requirement</div>
                                        <ul className='carDetailChecklist'>
                                            {rentalConditions.drivers_age.map((line, index) => (
                                                <li key={index}><img src={GreenTick} alt="" /> {line}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {rentalConditions?.fuel_policy?.length > 0 && (
                                    <div className='carDetail__infoBlock'>
                                        <div className='carDetail__infoTitle'>Fuel policy</div>
                                        <ul className='carDetailChecklist'>
                                            {rentalConditions.fuel_policy.map((line, index) => (
                                                <li key={index}><img src={GreenTick} alt="" /> {line}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className='carDetail__infoBlock carDetail__infoBlock--last'>
                                    <div className='carDetail__infoTitle'>Car has unlimited mileage</div>
                                    <p className='carDetail__infoDesc'>There is no limit on how many kilometers/miles can be traveled.</p>
                                </div>
                            </div>
                        </div>

                        {rentalConditionsExtra.length > 0 && (
                            <div className='col-md-12'>
                                <div className='bgWhite commonBorder borderRadius20 paddingCommon20'>
                                    
                                    <div className='fareDetailsHead ' onClick={() => setConditionsOpen(!conditionsOpen)}>
                                        <div className='bsSectionTitle'>Rental Conditions &amp; Policies</div>
                                        <span className={`fareCollapseIcon ${conditionsOpen ? 'open' : ''}`}>&#8963;</span>
                                    </div>
                                    {conditionsOpen && (
                                        <div className='mt-4'>
                                            {rentalConditionsExtra.map(([key, lines], index) => (
                                                <div
                                                    className={`carDetail__infoBlock ${index === rentalConditionsExtra.length - 1 ? 'carDetail__infoBlock--last' : ''}`}
                                                    key={key}
                                                >
                                                    <div className='carDetail__infoTitle'>{formatConditionTitle(key)}</div>
                                                    <ul className='carDetailChecklist'>
                                                        {lines.map((line, lineIndex) => (
                                                            <li key={lineIndex}><img src={GreenTick} alt="" /> {line}</li>
                                                        ))}
                                                    </ul>
                                                </div>       
                                                ))}                                
                                        </div>
                                    
                                    )}
                                </div>
                                
                            </div>
                        )}

                    </div>
                </div>
            </section>
        </>
    )
}

export default CarDetail
